import React, {useEffect} from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import {useNavigate, useSearchParams} from "react-router-dom";
import Alert, {AlertProps} from "@src/common/alert";
import {GenerativeModelVariation, useGenerativeModelAPI} from "../../api/generative_model_api";

interface VaultVariationIndexProps {
}

const VaultVariationIndex: React.FC<VaultVariationIndexProps> = (props) => {
    const [queryParams] = useSearchParams();

    const [data, setData] = React.useState<GenerativeModelVariation[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const genModelAPI = useGenerativeModelAPI();
    const navigate = useNavigate();

    const columns = [
        {
            name: "Base Model",
            selector: (row: GenerativeModelVariation) => row.base_model || "",
            sortable: true,
            width: "150px"
        },
        {
            name: "Status",
            selector: (row: GenerativeModelVariation) => row.status || "",
            sortable: true,
            width: "150px"
        },
        {
            name: "Is Default",
            selector: (row: GenerativeModelVariation) => row.is_default === true ? "Yes" : "No",
            sortable: true,
            width: "150px"
        },
        {
            name: "Score",
            selector: (row: GenerativeModelVariation) => row.evaluation_score || "",
            sortable: true,
            width: "150px"
        },
        {
            name: "Config",
            selector: (row: GenerativeModelVariation) => row.config || "",
            sortable: true
        },
        {
            name: "Actions",
            cell: (row: GenerativeModelVariation) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleMakeDefault(row)}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
                    >
                        Toggle Default
                    </button>
                    <button
                        onClick={() => handleStatusToggle(row)}
                        className={`${row.status ? "bg-yellow-500 hover:bg-yellow-700" : "bg-green-500 hover:bg-green-700"} text-white font-bold py-1 px-3 rounded text-sm`}
                    >
                        {row.status === "ACTIVE" ? "Deactivate" : "Activate"}
                    </button>
                </div>
            )
        }
    ];

    const fetchVaults = () => {
        if (!queryParams.get("generative_model_id")) {
            return;
        }
        setLoading(true);

        // @ts-ignore
        genModelAPI.getVariationsForModel(queryParams.get("generative_model_id"))
            .then((response) => {
                setData(response);
                setLoading(false);
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: "Failed to load vaults: " + error.message
                });
                setLoading(false);
            });
    };

    const handleMakeDefault = (vault: GenerativeModelVariation) => {
        if (!vault.id) return;

        genModelAPI.updateModelVariation(vault.id, {...vault, is_default: !vault.is_default})
            .then(() => {
                setStatus({
                    type: "success",
                    message: "Vault set as default successfully"
                });
                fetchVaults();
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: "Failed to set vault as default: " + error.message
                });
            });
    };

    const handleStatusToggle = (vault: GenerativeModelVariation) => {
        if (!vault.id) return;

        const newStatus = vault.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        genModelAPI.updateModelVariation(vault.id, {...vault, status: newStatus})
            .then(() => {
                setStatus({
                    type: "success",
                    message: `Vault ${newStatus === "ACTIVE" ? "activated" : "deactivated"} successfully`
                });
                fetchVaults();
            })
            .catch((error) => {
                setStatus({
                    type: "error",
                    message: `Failed to ${newStatus === "ACTIVE" ? "activate" : "deactivate"} vault: ${error.message}`
                });
            });
    };


    useEffect(() => {
        fetchVaults();
    }, [queryParams]);

    return (
        <AppContainer>
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">Vaults</h1>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)}/>
                    </div>
                )}

                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    progressPending={loading}
                    progressComponent={<div className="py-6">Loading variations...</div>}
                    noDataComponent={<div className="py-6">No variations found</div>}
                />
            </div>
        </AppContainer>
    );
};

export default VaultVariationIndex;